/**
 * Client-side brochure PDF processing.
 *
 * All heavy lifting happens in the browser via pdf.js: page rasterization
 * (preview tier for the server-side vision pre-pass, hi-res tier for logo
 * cropping) and embedded photo extraction. The server never touches the PDF
 * bytes — it only sees hosted JPEG/PNG renders, so no Node canvas/wasm shims
 * are needed on the backend.
 */

export const BROCHURE_LIMITS = {
  maxBytes: 25 * 1024 * 1024,
  maxPages: 40,
  previewEdge: 1280,
  hiResEdge: 2600,
  thumbEdge: 320,
  maxEmbedded: 12,
  minEmbeddedEdge: 200,
  embeddedMaxEdge: 1920,
  // Embedded photos upload under the 'assets' kind (server cap 5MB). Stay
  // safely under it so a large PNG doesn't 400 and fail the whole first send.
  embeddedMaxBytes: 4.5 * 1024 * 1024,
} as const;

export type BrochureErrorCode = 'encrypted' | 'too-large' | 'too-many-pages' | 'render-error';

export class BrochureError extends Error {
  constructor(
    readonly code: BrochureErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'BrochureError';
  }
}

export interface BrochureProgress {
  phase: 'loading' | 'rendering' | 'extracting';
  page?: number;
  total?: number;
}

/** Composer-chip state for the (single) attached brochure. */
export interface BrochureAttachment {
  file: File;
  status: 'processing' | 'ready' | 'uploading' | 'error';
  progress?: BrochureProgress;
  artifacts?: BrochureArtifacts;
  error?: string;
}

export interface BrochureArtifacts {
  pageCount: number;

  /** JPEG renders of every page (1280px longest edge), in page order. */
  pagePreviews: File[];

  /** Hi-res renders (2600px longest edge) of pages 1, 2 and the last page. */
  pageHiRes: { pageNumber: number; file: File }[];

  /** Embedded raster photos, deduped, largest-first, re-encoded to JPEG/PNG. */
  embeddedImages: File[];

  /** Small data-URL of page 1 for the composer chip. */
  page1Thumb: string;

  warnings: string[];
}

/** Pages that also get a hi-res render (logos live on covers and back pages). */
function hiResPageNumbers(pageCount: number): number[] {
  return [...new Set([1, Math.min(2, pageCount), pageCount])];
}

export async function processBrochurePdf(
  file: File,
  onProgress: (progress: BrochureProgress) => void,
): Promise<BrochureArtifacts> {
  if (file.size > BROCHURE_LIMITS.maxBytes) {
    throw new BrochureError(
      'too-large',
      `PDF is ${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is ${BROCHURE_LIMITS.maxBytes / 1024 / 1024}MB`,
    );
  }

  onProgress({ phase: 'loading' });

  // Dynamic import keeps pdf.js (~400KB + worker) out of the main bundle.
  const pdfjs = await import('pdfjs-dist');
  const { default: workerUrl } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const loadingTask = pdfjs.getDocument({
    data: await file.arrayBuffer(),
    // Copied unhashed into public/_pdfjs by scripts/copy-pdfjs-assets.mjs
    // (predev/prebuild) — pdf.js resolves wasm decoders (JPX/JBIG2), cmaps and
    // fonts by fixed filename.
    wasmUrl: '/_pdfjs/wasm/',
    cMapUrl: '/_pdfjs/cmaps/',
    cMapPacked: true,
    standardFontDataUrl: '/_pdfjs/standard_fonts/',
  });

  let pdf;

  try {
    pdf = await loadingTask.promise;
  } catch (err: any) {
    if (err?.name === 'PasswordException') {
      throw new BrochureError('encrypted', 'This PDF is password-protected');
    }

    throw new BrochureError('render-error', `Couldn't read this PDF: ${err?.message ?? err}`);
  }

  try {
    if (pdf.numPages > BROCHURE_LIMITS.maxPages) {
      throw new BrochureError(
        'too-many-pages',
        `PDF has ${pdf.numPages} pages — the limit is ${BROCHURE_LIMITS.maxPages}`,
      );
    }

    const warnings: string[] = [];
    const pagePreviews: File[] = [];
    const pageHiRes: { pageNumber: number; file: File }[] = [];
    const hiResPages = hiResPageNumbers(pdf.numPages);
    let page1Thumb = '';

    const embeddedCandidates: { area: number; blob: Blob; ext: string }[] = [];
    const seenImageKeys = new Set<string>();
    let failedEmbedded = 0;

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      onProgress({ phase: 'rendering', page: pageNumber, total: pdf.numPages });

      const page = await pdf.getPage(pageNumber);

      try {
        const previewCanvas = renderTargetFor(page, BROCHURE_LIMITS.previewEdge);
        await page.render(previewCanvas.renderParams).promise;

        // Pages are opaque JPEG (hasAlpha=false). Route through the same
        // byte-budget path as embedded images so a photo-heavy page can't
        // exceed the server's 5MB 'brochure-pages' upload cap.
        const previewEncoded = await encodeUnderByteBudget(
          previewCanvas.canvas,
          false,
          BROCHURE_LIMITS.embeddedMaxBytes,
        );

        if (!previewEncoded) {
          throw new BrochureError('render-error', `Page ${pageNumber} preview exceeds the upload size limit`);
        }

        pagePreviews.push(
          new File([previewEncoded.blob], pageFileName(pageNumber, 'jpg'), {
            type: 'image/jpeg',
          }),
        );

        if (pageNumber === 1) {
          page1Thumb = thumbDataUrl(previewCanvas.canvas, BROCHURE_LIMITS.thumbEdge);
        }

        if (hiResPages.includes(pageNumber)) {
          const hiResCanvas = renderTargetFor(page, BROCHURE_LIMITS.hiResEdge);
          await page.render(hiResCanvas.renderParams).promise;

          const hiResEncoded = await encodeUnderByteBudget(hiResCanvas.canvas, false, BROCHURE_LIMITS.embeddedMaxBytes);

          if (!hiResEncoded) {
            throw new BrochureError('render-error', `Page ${pageNumber} hi-res render exceeds the upload size limit`);
          }

          pageHiRes.push({
            pageNumber,
            file: new File([hiResEncoded.blob], pageFileName(pageNumber, 'jpg', 'hires-'), { type: 'image/jpeg' }),
          });
        }

        // Embedded photo extraction. Rendering above resolves the image
        // objects, so getOperatorList() sees warm caches.
        onProgress({ phase: 'extracting', page: pageNumber, total: pdf.numPages });

        const extracted = await extractPageImages(page, pdfjs, seenImageKeys);
        embeddedCandidates.push(...extracted.images);
        failedEmbedded += extracted.failed;
      } finally {
        page.cleanup();
      }
    }

    if (failedEmbedded > 0) {
      warnings.push(`${failedEmbedded} embedded image(s) could not be decoded and were skipped`);
    }

    const embeddedImages = embeddedCandidates
      .sort((a, b) => b.area - a.area)
      .slice(0, BROCHURE_LIMITS.maxEmbedded)
      .map(
        (c, i) =>
          new File([c.blob], `brochure-photo-${String(i + 1).padStart(2, '0')}.${c.ext}`, {
            type: c.ext === 'png' ? 'image/png' : 'image/jpeg',
          }),
      );

    return { pageCount: pdf.numPages, pagePreviews, pageHiRes, embeddedImages, page1Thumb, warnings };
  } finally {
    await loadingTask.destroy();
  }
}

function pageFileName(pageNumber: number, ext: string, prefix = '') {
  return `${prefix}page-${String(pageNumber).padStart(2, '0')}.${ext}`;
}

function renderTargetFor(page: any, longestEdge: number) {
  const base = page.getViewport({ scale: 1 });
  const scale = longestEdge / Math.max(base.width, base.height);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);

  const canvasContext = canvas.getContext('2d')!;

  return { canvas, renderParams: { canvasContext, canvas, viewport } };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))), type, quality);
  });
}

function thumbDataUrl(source: HTMLCanvasElement, edge: number): string {
  const scale = edge / Math.max(source.width, source.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(source.width * scale);
  canvas.height = Math.round(source.height * scale);
  canvas.getContext('2d')!.drawImage(source, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/jpeg', 0.7);
}

/**
 * Pull decoded raster XObjects off a rendered page. pdf.js hands back either
 * an ImageBitmap wrapper or raw `{ data, width, height, kind }` depending on
 * platform support — handle both, re-encode to JPEG (PNG when alpha present),
 * and dedupe repeats (page backgrounds recur on every page).
 */
async function extractPageImages(
  page: any,
  pdfjs: any,
  seenKeys: Set<string>,
): Promise<{ images: { area: number; blob: Blob; ext: string }[]; failed: number }> {
  const images: { area: number; blob: Blob; ext: string }[] = [];
  let failed = 0;

  let opList;

  try {
    opList = await page.getOperatorList();
  } catch {
    return { images, failed: 1 };
  }

  const objIds = new Set<string>();

  for (let i = 0; i < opList.fnArray.length; i++) {
    if (opList.fnArray[i] === pdfjs.OPS.paintImageXObject || opList.fnArray[i] === pdfjs.OPS.paintImageXObjectRepeat) {
      const objId = opList.argsArray[i]?.[0];

      if (typeof objId === 'string') {
        objIds.add(objId);
      }
    }
  }

  for (const objId of objIds) {
    try {
      const imgData = await resolvePageObject(page, objId);

      if (!imgData) {
        continue;
      }

      const width: number = imgData.width ?? imgData.bitmap?.width ?? 0;
      const height: number = imgData.height ?? imgData.bitmap?.height ?? 0;

      if (Math.min(width, height) < BROCHURE_LIMITS.minEmbeddedEdge) {
        continue;
      }

      const { canvas, hasAlpha } = drawImageData(imgData, width, height, pdfjs);

      // Repeated backgrounds get separate per-page object IDs — dedupe on
      // dimensions plus a sparse pixel sample of the decoded output.
      const key = imageContentKey(canvas, width, height);

      if (seenKeys.has(key)) {
        continue;
      }

      seenKeys.add(key);

      const encoded = await encodeUnderByteBudget(canvas, hasAlpha, BROCHURE_LIMITS.embeddedMaxBytes);

      if (!encoded) {
        // Couldn't get it under the upload cap even at min JPEG quality — skip
        // rather than let the server reject it and fail the whole send.
        failed++;
        continue;
      }

      images.push({ area: width * height, blob: encoded.blob, ext: encoded.ext });
    } catch {
      failed++;
    }
  }

  return { images, failed };
}

/**
 * Encode a decoded photo so its blob stays under `maxBytes` (the server's
 * per-file upload cap). Transparent images start as PNG; if that's too big
 * they're flattened onto white and re-encoded as JPEG at descending quality.
 * Returns null if even the lowest-quality JPEG won't fit.
 */
async function encodeUnderByteBudget(
  canvas: HTMLCanvasElement,
  hasAlpha: boolean,
  maxBytes: number,
): Promise<{ blob: Blob; ext: 'png' | 'jpg' } | null> {
  if (hasAlpha) {
    const png = await canvasToBlob(canvas, 'image/png');

    if (png.size <= maxBytes) {
      return { blob: png, ext: 'png' };
    }
    // Fall through: flatten alpha onto white and try JPEG.
  }

  const source = hasAlpha ? flattenOntoWhite(canvas) : canvas;

  for (const quality of [0.85, 0.7, 0.55, 0.4]) {
    const jpg = await canvasToBlob(source, 'image/jpeg', quality);

    if (jpg.size <= maxBytes) {
      return { blob: jpg, ext: 'jpg' };
    }
  }

  return null;
}

/** Composite a possibly-transparent canvas onto an opaque white background. */
function flattenOntoWhite(source: HTMLCanvasElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;

  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0);

  return canvas;
}

/** `objs.get` throws when unresolved; the callback form + timeout avoids hangs. */
function resolvePageObject(page: any, objId: string): Promise<any | null> {
  const store = page.objs.has(objId) ? page.objs : page.commonObjs.has(objId) ? page.commonObjs : null;

  if (!store) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 3000);

    try {
      store.get(objId, (data: any) => {
        clearTimeout(timeout);
        resolve(data);
      });
    } catch {
      clearTimeout(timeout);
      resolve(null);
    }
  });
}

function drawImageData(
  imgData: any,
  width: number,
  height: number,
  pdfjs: any,
): { canvas: HTMLCanvasElement; hasAlpha: boolean } {
  const maxEdge = BROCHURE_LIMITS.embeddedMaxEdge;
  const scale = Math.min(1, maxEdge / Math.max(width, height));

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);

  const ctx = canvas.getContext('2d')!;
  let hasAlpha = false;

  if (imgData.bitmap) {
    ctx.drawImage(imgData.bitmap, 0, 0, canvas.width, canvas.height);
    hasAlpha = sampleHasAlpha(ctx, canvas.width, canvas.height);
  } else if (imgData.data) {
    const rgba = toRgba(imgData, width, height, pdfjs);
    hasAlpha = rgba.hasAlpha;

    const full = document.createElement('canvas');
    full.width = width;
    full.height = height;
    full.getContext('2d')!.putImageData(new ImageData(rgba.data, width, height), 0, 0);
    ctx.drawImage(full, 0, 0, canvas.width, canvas.height);
  } else {
    throw new Error(`Unknown image object shape for scaling: ${Object.keys(imgData).join(',')}`);
  }

  return { canvas, hasAlpha };
}

function toRgba(
  imgData: any,
  width: number,
  height: number,
  pdfjs: any,
): { data: Uint8ClampedArray; hasAlpha: boolean } {
  const imageKinds = pdfjs.ImageKind;
  const src: Uint8ClampedArray = imgData.data;
  const out = new Uint8ClampedArray(width * height * 4);
  let hasAlpha = false;

  switch (imgData.kind) {
    case imageKinds.RGBA_32BPP: {
      out.set(src);

      for (let i = 3; i < out.length; i += 4) {
        if (out[i] < 255) {
          hasAlpha = true;
          break;
        }
      }
      break;
    }
    case imageKinds.RGB_24BPP: {
      for (let p = 0, s = 0; p < out.length; p += 4, s += 3) {
        out[p] = src[s];
        out[p + 1] = src[s + 1];
        out[p + 2] = src[s + 2];
        out[p + 3] = 255;
      }
      break;
    }
    case imageKinds.GRAYSCALE_1BPP: {
      const rowBytes = (width + 7) >> 3;

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const bit = (src[y * rowBytes + (x >> 3)] >> (7 - (x & 7))) & 1;
          const v = bit ? 255 : 0;
          const p = (y * width + x) * 4;
          out[p] = out[p + 1] = out[p + 2] = v;
          out[p + 3] = 255;
        }
      }
      break;
    }
    default:
      throw new Error(`Unsupported ImageKind: ${imgData.kind}`);
  }

  return { data: out, hasAlpha };
}

function sampleHasAlpha(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
  const step = Math.max(1, Math.floor(Math.min(width, height) / 16));

  for (let y = 0; y < height; y += step) {
    const row = ctx.getImageData(0, y, width, 1).data;

    for (let x = 3; x < row.length; x += 4 * step) {
      if (row[x] < 255) {
        return true;
      }
    }
  }

  return false;
}

/** Cheap content fingerprint: dimensions + 64 sampled pixels. */
function imageContentKey(canvas: HTMLCanvasElement, width: number, height: number): string {
  const ctx = canvas.getContext('2d')!;
  const sw = canvas.width;
  const sh = canvas.height;
  let hash = 0;

  for (let i = 0; i < 64; i++) {
    const x = Math.floor(((i % 8) / 8) * sw);
    const y = Math.floor((Math.floor(i / 8) / 8) * sh);
    const px = ctx.getImageData(x, y, 1, 1).data;
    hash = (hash * 31 + px[0] * 7 + px[1] * 5 + px[2] * 3 + px[3]) >>> 0;
  }

  return `${width}x${height}:${hash}`;
}
